import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { airtableService } from '../services/airtable';

const DrGrantLogin = ({ onLogin }) => {
  const [step, setStep] = useState('auth'); // 'auth' or 'airtable-setup'
  const [formData, setFormData] = useState({
    doctorPassword: '',
    airtableApiKey: '',
    airtableBaseId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const DOCTOR_PASSWORD = process.env.REACT_APP_DOCTOR_PASSWORD || 'DrGrant2024!'; // In production, this should be more secure

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.doctorPassword !== DOCTOR_PASSWORD) {
      setError('Invalid password');
      return;
    }

    // Check if Airtable is already configured
    const configured = airtableService.loadSavedConfig();
    if (configured) {
      // Log in first, then let the dashboard handle table setup
      const userData = {
        name: 'Dr. Grant',
        role: 'doctor'
      };
      console.log('🎯 Logging in Dr. Grant - table setup will be handled on dashboard');
      onLogin(userData, 'doctor');
    } else {
      setStep('airtable-setup');
    }
  };

  const handleAirtableSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.airtableApiKey.trim() || !formData.airtableBaseId.trim()) {
      setError('Please provide both Airtable API Key and Base ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = airtableService.initialize(
        formData.airtableApiKey.trim(),
        formData.airtableBaseId.trim()
      );

      if (success) {
        // Skip table setup, let dashboard handle it
        console.log('Airtable connected successfully, proceeding to dashboard');
        const userData = {
          name: 'Dr. Grant',
          role: 'doctor'
        };
        onLogin(userData, 'doctor');
      } else {
        setError('Failed to connect to Airtable. Please check your credentials.');
      }
    } catch (error) {
      console.error('Airtable setup error:', error);
      setError(`Setup failed: ${error.message || 'Unable to connect to Airtable. Please verify your API key and Base ID.'}`);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'airtable-setup') {
    return (
      <div className="container">
        <div className="header">
          <h1>NotesNCodes</h1>
          <p>Airtable Setup Required</p>
        </div>
        
        <div className="card">
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: 'var(--text-dark)', marginBottom: '12px' }}>
              Connect to Airtable
            </h3>
            <p style={{ color: 'var(--text-light)', marginBottom: '16px' }}>
              To use NotesNCodes, you need to connect your Airtable account. 
              Please provide your Airtable API credentials below.
            </p>
            
            <div style={{ 
              padding: '16px', 
              backgroundColor: 'var(--warm-cream)', 
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h4 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>
                Setup Instructions:
              </h4>
              <ol style={{ color: 'var(--text-dark)', paddingLeft: '20px' }}>
                <li>Go to <a href="https://airtable.com/create/base" target="_blank" rel="noopener noreferrer">airtable.com</a> and create a new base</li>
                <li>Copy the Base ID from your Airtable URL (starts with "app")</li>
                <li>Get your API key from <a href="https://airtable.com/account" target="_blank" rel="noopener noreferrer">Account Settings</a></li>
                <li><strong>The system will automatically create all required tables and fields!</strong></li>
                <li>No manual table setup needed - just provide your credentials below</li>
              </ol>
            </div>
            
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#e8f5e8', 
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              ✅ <strong>Auto-Setup:</strong> The app will create Sessions, Queue, and Evaluations tables with all required fields automatically.
            </div>
          </div>
          
          <form onSubmit={handleAirtableSubmit}>
            <div className="input-group">
              <label htmlFor="airtableApiKey">Airtable API Key</label>
              <input
                type="password"
                id="airtableApiKey"
                name="airtableApiKey"
                value={formData.airtableApiKey}
                onChange={handleChange}
                placeholder="Enter your Airtable API key"
                disabled={loading}
              />
              <small style={{ color: 'var(--text-light)' }}>
                Found in your Airtable Account Settings
              </small>
            </div>
            
            <div className="input-group">
              <label htmlFor="airtableBaseId">Airtable Base ID</label>
              <input
                type="text"
                id="airtableBaseId"
                name="airtableBaseId"
                value={formData.airtableBaseId}
                onChange={handleChange}
                placeholder="Enter your Airtable Base ID (e.g., appXXXXXXXXXX)"
                disabled={loading}
              />
              <small style={{ color: 'var(--text-light)' }}>
                Found in your Airtable base URL
              </small>
            </div>
            
            {error && (
              <div style={{ 
                color: 'var(--coral)', 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#ffe6e6', 
                borderRadius: '6px' 
              }}>
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? 'Connecting...' : 'Connect to Airtable'}
            </button>
          </form>
          
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button 
              onClick={() => setStep('auth')} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--text-light)', 
                cursor: 'pointer' 
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>NotesNCodes</h1>
        <p>👩‍⚕️ Dr. Grant Login</p>
        <small style={{ color: 'var(--text-light)' }}>For managing sessions and evaluating students</small>
      </div>
      
      <div className="card">
        <form onSubmit={handleAuthSubmit}>
          <div className="input-group">
            <label htmlFor="doctorPassword">Password</label>
            <input
              type="password"
              id="doctorPassword"
              name="doctorPassword"
              value={formData.doctorPassword}
              onChange={handleChange}
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>
          
          {error && (
            <div style={{ 
              color: 'var(--coral)', 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#ffe6e6', 
              borderRadius: '6px' 
            }}>
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link to="/student/login" style={{ color: 'var(--text-light)' }}>
            Student Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DrGrantLogin;